import { apiCall } from "@/common/api/ApiResult.model";
import { POPULATION_TYPE } from "@/common/models";
import { SharedState } from "@/common/state";
import { percentage } from "@/fp";
import { querifyArray as querifyUrlArray } from "@/utils/url";

import axios from "axios";

import {
  FamiliarityEntityType,
  FamiliarityItem,
  FamiliarityLevel,
  FamiliarityLevelColors,
  FamiliarityStatus,
} from "./models";

const get_affinity_from_score = percentage(25);

const api_entity_map: Record<FamiliarityEntityType, string> = {
  [POPULATION_TYPE.EMPLOYEE]: "Person",
  [POPULATION_TYPE.PRIVATE_EMAIL]: "Person",
  [POPULATION_TYPE.ORGANIZATION]: "Organization",
  [POPULATION_TYPE.DEPARTMENT]: "Department",
};

const display_percentage = (total: number) => (num: number) => {
  return ((num / total) * 100).toFixed(1).replace(".0", "");
};

const get_departments_name_from_entityLabelIds = (entityLabelIds: string[]) => {
  const arr = entityLabelIds.filter((id) => SharedState.departments.value[id]);
  return arr.length > 0 ? SharedState.departments.value[arr[0]] : "";
};

const result_mapper = (type: FamiliarityEntityType) => (item: any) => {
  const {
    entityExposedByCount: from_count = 0,
    entityExposedToCount: to_count = 0,
  } = item;
  const total = from_count + to_count;
  const normalized_event_count = total === 0 ? 1 : total;
  const get_percentage = display_percentage(normalized_event_count);
  return {
    ...item,
    firstEventAt: item.firstEventAt,
    lastEventAt: item.lastEventAt,
    eventCount: item.eventCount,
    type,
    exposer_balance: {
      from: get_percentage(from_count),
      to: get_percentage(to_count),
    },
    level_color: FamiliarityLevelColors[item.level as FamiliarityLevel],
    email: item.entityIdForDisplay || item.entityId,
    department:
      type === POPULATION_TYPE.EMPLOYEE
        ? get_departments_name_from_entityLabelIds(item.entityLabelIds)
        : "",
    affinity: get_affinity_from_score(item.score > 25 ? 25 : item.score),
  };
};

const map_departments = (status: FamiliarityStatus) => (item: any) => {
  return {
    id: item.entityId,
    entityId: item.entityId,
    name: item.entityName,
    labelId: item.labelId,
    status,
    level: item.level,
    level_color: FamiliarityLevelColors[item.level as FamiliarityLevel],
  };
};

const is_record = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const to_number = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim() && Number.isFinite(+value)) {
    return +value;
  }
};

const extract_department_counter = (
  count_res: { data?: { value?: unknown } } | null,
  label_id: string,
  status: FamiliarityStatus
): number | undefined => {
  const value = count_res?.data?.value;
  const direct_count = to_number(value);
  if (typeof direct_count === "number") {
    return direct_count;
  }

  const counters = is_record(value) && Array.isArray(value.familiarityCounters)
    ? value.familiarityCounters
    : Array.isArray(value)
    ? value
    : null;

  if (counters) {
    const matching_counter = counters.find(
      (counter): counter is Record<string, unknown> =>
        is_record(counter) &&
        counter.labelId === label_id && counter.userSetLabelId === status
    );
    return to_number(matching_counter?.itemCount) || 0;
  }

  if (!is_record(value)) {
    return;
  }

  return [
    value.itemCount,
    value.count,
    value.totalCount,
    value.total,
    value.departmentFamiliaritiesCount,
  ]
    .map(to_number)
    .find((count) => typeof count === "number");
};

const get_department_count_url = (
  label_id: string,
  status: FamiliarityStatus
) => {
  const count_qparams = [
    querifyUrlArray({ userSetLabelIds: [status] }),
    querifyUrlArray({ labelIds: [label_id] }),
    querifyUrlArray({ entityTypes: ["department"] }),
  ].join("&");
  return `/extproxy/integrations/familiarity-counters?${count_qparams}`;
};

/* 
 TODO: 

entityLabelId = depatrtmny_id => only employees from that department


 for DEPARTMENTS => GET /department-familiarities
 with  params 
      userSetLabelIds[]:  untrusted
      labelId: d9fe0aba-6b36-4ac5-8f58-4f5d3b03a701
      includeCount: true
      labelIds[] => for the get_familiarities_for_entity use case

changing trust for department for Financial (ex)

 PUT '/integrations-user/department-familiarities/userSetLabelId'
  {
      entityId: entity_id,
      labelId: label_id,
      userSetLabelId: status,
  }
*/

export const get_user_email_from_internal_entity_id = apiCall<string, string>(
  "familiarity-get-user-email",
  async (internal_entity_id) => {
    // https://qa-webapi.cognni.ai/extproxy/integrations/simplified-familiarity-aggregations
    const res = await axios.get(
      `/extproxy/integrations/simplified-familiarity-aggregations?internalEntityId=${internal_entity_id}&limit=1`,
    );
    const item = res.data.value.familiarityAggregations[0];
    return {
      success: true,
      value: item ? item.entityId : internal_entity_id,
    };
  },
);

export const get_entities_for_status = apiCall<
  {
    label_id: string;
    entity_type: FamiliarityEntityType;
    department_id: string;
    cursor: string;
    status: FamiliarityStatus;
  },
  { cursor: string; objects: FamiliarityItem[]; count: number }
>(
  "familiarity-get-entities-for-status",
  async ({ entity_type, label_id, cursor, department_id, status }) => {
    const params: any = {
      userSetLabelIds: querifyArray({ userSetLabelIds: [status] }),
      labelId: label_id,
      includeCount: true,
    };
    let end_point = "familiarity-aggregations";
    switch (entity_type) {
      case POPULATION_TYPE.EMPLOYEE:
        if (department_id !== "any") {
          params.entityLabelId = department_id;
        }
        params.cursor = cursor;
        //@ts-ignore
        params.entityType = api_entity_map[entity_type];
        break;
      case POPULATION_TYPE.DEPARTMENT:
        end_point = "department-familiarities";
        params.cursor = cursor;
        break;
      case POPULATION_TYPE.PRIVATE_EMAIL:
        params.cursor = cursor;
        //@ts-ignore
        params.entityType = api_entity_map[entity_type];
        params.includeExternalEntitiesOnly = true;
        params.includePersonalAddressEntitiesOnly = true;
        break;
      default:
        params.cursor = cursor;
        //@ts-ignore
        params.entityType = api_entity_map[entity_type];
        break;
    }

    const qparams = Object.entries(params)
      .map(([key, val]) => `${key}=${val}`)
      .join("&");

    // For departments, `itemCount` in the list response reflects only the
    // current page size. Use the counters endpoint for the real total while
    // keeping department-familiarities as the paginated list source.
    const department_count_url =
      entity_type === POPULATION_TYPE.DEPARTMENT
        ? get_department_count_url(label_id, status)
        : null;

    const list_promise = axios.get(
      `/extproxy/integrations/${end_point}?${qparams}`,
    );
    const count_promise: Promise<any> = department_count_url
      ? axios.get(department_count_url).catch(() => null)
      : Promise.resolve(null);

    const [res, department_count_res] = await Promise.all([
      list_promise,
      count_promise,
    ]);

    const {
      familiarityAggregations,
      departmentFamiliarities,
      cursor: new_cursor = "",
      itemCount,
    } = res.data.value;

    const objects =
      entity_type === POPULATION_TYPE.DEPARTMENT
        ? (departmentFamiliarities || []).map(map_departments(status))
        : (familiarityAggregations || []).map(result_mapper(entity_type));

    let count: number = itemCount;
    if (entity_type === POPULATION_TYPE.DEPARTMENT) {
      const parsed_total = extract_department_counter(
        department_count_res,
        label_id,
        status
      );

      if (typeof parsed_total === "number") {
        count = parsed_total;
      } else {
        // Fallback when the endpoint doesn't support useCount=true: at least
        // report "loaded so far + 1 more" while a cursor exists so the badge
        // doesn't lie about a hard 100 cap.
        count = new_cursor
          ? (itemCount || objects.length) + 1
          : itemCount || objects.length;
      }
    }

    return {
      success: true,
      value: {
        count,
        cursor: new_cursor,
        objects,
      },
    };
  },
);

export const get_organization_entities_for_status = apiCall<
  {
    label_id: string;
    cursor: string;
    status: FamiliarityStatus;
  },
  { cursor: string; objects: FamiliarityItem[]; count: number }
>(
  "familiarity-get-organization-entities-for-status",
  async (payload: {
    label_id: string;
    cursor: string;
    status: FamiliarityStatus;
  }) => {
    const { label_id, cursor, status } = payload;
    const list_params = new URLSearchParams({
      userSetLabelId: status,
      labelId: label_id,
      cursor,
      entityType: "organization",
    });
    const count_params = new URLSearchParams({
      userSetLabelId: status,
      labelId: label_id,
      cursor: "",
      entityType: "organization",
      useCount: "true",
    });

    const [list_res, count_res] = await Promise.all([
      axios.get(
        `/extproxy/integrations/simplified-familiarity-aggregations?${list_params.toString()}`,
      ),
      axios.get(
        `/extproxy/integrations/simplified-familiarity-aggregations?${count_params.toString()}`,
      ),
    ]);

    const {
      familiarityAggregations = [],
      cursor: new_cursor = "",
    } = list_res.data.value || {};
    const count_value = count_res.data.value || {};
    const parsed_count = [
      count_value.itemCount,
      count_value.count,
      count_value.totalCount,
      count_value.familiarityAggregationsCount,
      count_value.total,
    ].find((v) => typeof v === "number");
    return {
      success: true,
      value: {
        count: parsed_count ?? familiarityAggregations.length,
        cursor: new_cursor,
        objects: familiarityAggregations.map(
          result_mapper(POPULATION_TYPE.ORGANIZATION),
        ),
      },
    };
  },
);

// Lowercase entity type map for the simplified endpoint (case-sensitive)
const simplified_entity_map: Partial<Record<FamiliarityEntityType, string>> = {
  [POPULATION_TYPE.EMPLOYEE]: "person",
  [POPULATION_TYPE.PRIVATE_EMAIL]: "person",
  [POPULATION_TYPE.ORGANIZATION]: "organization",
};

export const get_simplified_entities_for_status = apiCall<
  {
    label_id: string;
    entity_type: FamiliarityEntityType;
    cursor: string;
    status: FamiliarityStatus;
    department_id?: string;
  },
  { cursor: string; objects: FamiliarityItem[]; count: number }
>(
  "familiarity-get-entities-for-status",
  async ({
    label_id,
    entity_type,
    cursor,
    status,
    department_id,
  }: {
    label_id: string;
    entity_type: FamiliarityEntityType;
    cursor: string;
    status: FamiliarityStatus;
    department_id?: string;
  }) => {
    const entityType = simplified_entity_map[entity_type] || "person";
    const list_params = new URLSearchParams({
      userSetLabelId: status,
      labelId: label_id,
      cursor,
      entityType,
    });
    if (entity_type === POPULATION_TYPE.EMPLOYEE) {
      list_params.set("isEntityExternal", "false");
      if (department_id && department_id !== "any") {
        list_params.set("entityLabelId", department_id);
      }
    }
    if (entity_type === POPULATION_TYPE.PRIVATE_EMAIL) {
      list_params.set("isEntityExternal", "true");
      list_params.set("isEntityPersonalAddress", "true");
    }
    const count_params = new URLSearchParams({
      userSetLabelId: status,
      labelId: label_id,
      cursor: "",
      entityType,
      useCount: "true",
    });
    if (entity_type === POPULATION_TYPE.EMPLOYEE) {
      count_params.set("isEntityExternal", "false");
      if (department_id && department_id !== "any") {
        count_params.set("entityLabelId", department_id);
      }
    }
    if (entity_type === POPULATION_TYPE.PRIVATE_EMAIL) {
      count_params.set("isEntityExternal", "true");
      count_params.set("isEntityPersonalAddress", "true");
    }

    const [list_res, count_res] = await Promise.all([
      axios.get(
        `/extproxy/integrations/simplified-familiarity-aggregations?${list_params.toString()}`
      ),
      axios.get(
        `/extproxy/integrations/simplified-familiarity-aggregations?${count_params.toString()}`
      ),
    ]);

    const {
      familiarityAggregations = [],
      cursor: new_cursor = "",
    } = list_res.data.value || {};
    // useCount=true returns the count as a plain number: { "ok": true, "value": 15 }
    const parsed_count =
      typeof count_res.data.value === "number"
        ? count_res.data.value
        : familiarityAggregations.length;

    return {
      success: true,
      value: {
        count: parsed_count,
        cursor: new_cursor,
        objects: familiarityAggregations.map(result_mapper(entity_type)),
      },
    };
  }
);

/* export const get_trusted_entities = get_entities_for_status("trusted");
export const get_untrusted_entities = get_entities_for_status("untrusted");
export const get_unset_entities = get_entities_for_status("unmanaged"); */

/**
 * @desc
 *  ### userSetLabelId: trusted
 *  the designated label as well as labels of the same infoType with lower sensitivity get userSetLabelId = trusted
 *  labels of the same infoType with higher sensitivity get userSetLabelId = untrusted
 *
 *  ### userSetLabelId: untrusted
 *  the designated label as well as labels of the same infoType with higher sensitivity get userSetLabelId = untrusted
 *  labels of the same infoType with lower sensitivity get userSetLabelId = trusted
 *
 *  ### userSetLabelId: unmanaged
 *  the designated label as well as labels of the same infoType get userSetLabelId = unmanaged
 */
export const change_entity_familiarity_status = apiCall<
  {
    entity_type: FamiliarityEntityType;
    entity_id: string;
    label_id: string;
    status: FamiliarityStatus;
  },
  boolean
>(
  "familiarity-update-entity-status",
  async ({ entity_type, entity_id, label_id, status }) => {
    const url =
      entity_type === POPULATION_TYPE.DEPARTMENT
        ? "/extproxy/integrations-user/department-familiarities/userSetLabelId"
        : "/extproxy/integrations-user/familiarity-aggregations";

    await axios.put(url, {
      entityId: entity_id ? entity_id.toLowerCase() : "",
      labelId: label_id,
      userSetLabelId: status,
    });
    return {
      success: true,
      value: true,
    };
  },
);

function querifyArray(ref: any) {
  const [arrayName, array] = Object.entries(ref)?.[0] || [];
  if (!arrayName || !array) {
    throw new Error(`invalid arg: ${ref}`);
  }
  if (!Array.isArray(array)) {
    throw new Error(`${arrayName} must be an array`);
  }
  if (array.length == 0) return "";
  if (array.length === 1) {
    // we must not use a single item array, as it's converted to an expression that the api interprets as being a string rather than an array. Duplicates are handled in the receiving end
    const item = array[0];
    array.push(item);
  }
  return array.reduce((acc, item) => {
    const newItem = `&${arrayName}[]=${item}`;
    return acc + newItem;
  }, "");
}

export const get_entity_familiarities_status = apiCall<
  {
    entityId: string;
    labelIds: string[];
    type: FamiliarityEntityType;
  },
  Array<{
    labelId: string;
    status: FamiliarityStatus;
    affinity: number;
    entityId: string;
  }>
>("familiarity-get-entity-status", async ({ entityId, labelIds, type }) => {
  const params = {
    entityId: entityId ? entityId.toLowerCase() : "",
    labelIds: querifyArray({ labelIds }),
    entityType: api_entity_map[type],
  };
  if (type === POPULATION_TYPE.PRIVATE_EMAIL) {
    // @ts-ignore
    params.includeExternalEntitiesOnly = true;
    // @ts-ignore
    params.includePersonalAddressEntitiesOnly = true;
  }

  if (type === POPULATION_TYPE.ORGANIZATION) {
    // @ts-ignore
    params.includeExternalEntities = true;
  }

  const query_params = Object.entries(params)
    .map(([k, v]) => `${k}=${v}`)
    .join("&");
  console.log("query_params: ", query_params);
  const url = `/extproxy/integrations/${
    type === POPULATION_TYPE.DEPARTMENT
      ? "department-familiarities"
      : "familiarity-aggregations"
  }?`;
  const res = await axios.get(url + query_params);
  const { familiarityAggregations, departmentFamiliarities } = res.data.value;

  const arr =
    type === POPULATION_TYPE.DEPARTMENT
      ? departmentFamiliarities
      : familiarityAggregations;

  return {
    success: true,
    value: arr.map((item: any) => {
      return {
        labelId: item.labelId,
        entityId: item.internalEntityId,
        affinity: get_affinity_from_score(item.score > 25 ? 25 : item.score),
        status: item.userSetLabelId,
      };
    }),
  };
});
